import { MoveHorizontal, Share2, Zap } from "lucide-react";
import { FC, useState } from "react";
import { message } from "antd";
import { TInventory } from "~/types";
import { ConsolidateInventory } from "../consolidate";
import { DeconsolidateInventory } from "../deconsolidate";
import { QuickConsolidateModal } from "../consolidate/quick-consolidate-modal";
import { useConsolidateStore } from "../consolidate/consolidate.store";

type Props = {
  selectedInventories?: TInventory[];
  onActionComplete?: () => void;
};

export const ConsolidationActions: FC<Props> = ({
  selectedInventories = [],
  onActionComplete,
}) => {
  const [showConsolidateModal, setShowConsolidateModal] = useState(false);
  const [showDeconsolidateModal, setShowDeconsolidateModal] = useState(false);
  const [showQuickConsolidateModal, setShowQuickConsolidateModal] =
    useState(false);

  const consolidateStore = useConsolidateStore();

  const validateSelection = (
    items: TInventory[]
  ): { clientId: string; status: string } | null => {
    if (items.length === 0) return null;
    const clientIds = new Set(items.map((item) => item.clientId));
    if (clientIds.size > 1) {
      message.error(
        "Selected items must belong to the same client to consolidate."
      );
      return null;
    }
    const statuses = new Set(items.map((item) => item.status));
    if (statuses.size > 1) {
      message.error(
        "Selected items must all have the same status to consolidate."
      );
      return null;
    }
    return { clientId: items[0].clientId, status: items[0].status };
  };

  const openConsolidate = () => {
    if (selectedInventories.length > 0) {
      const result = validateSelection(selectedInventories);
      if (!result) return;
      // Pre-populate the store with client and selected pieces
      consolidateStore.setClientId(result.clientId);
      consolidateStore.setClientLocked(true);
      consolidateStore.setSelectedPieces(selectedInventories);
      // Start at step 1 (select container) since pieces are pre-selected
      consolidateStore.setStep(1);
    }
    setShowConsolidateModal(true);
  };

  const closeConsolidate = () => {
    setShowConsolidateModal(false);
    onActionComplete?.();
  };

  const openDeconsolidate = () => {
    setShowDeconsolidateModal(true);
  };

  const closeDeconsolidate = () => {
    setShowDeconsolidateModal(false);
    onActionComplete?.();
  };

  const openQuickConsolidate = () => {
    if (selectedInventories.length === 0) {
      message.warning("Please select pieces to quick consolidate.");
      return;
    }
    const result = validateSelection(selectedInventories);
    if (!result) return;
    setShowQuickConsolidateModal(true);
  };

  const hasSelection = selectedInventories.length > 0;

  return (
    <>
      <div className="mt-6">
        <div className="bg-white rounded-lg border p-4 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-medium">Inventory Actions</h3>
              <p className="text-sm text-gray-500 mt-1">
                {hasSelection
                  ? `${selectedInventories.length} item(s) selected — consolidate or deconsolidate`
                  : "Select items or use the buttons to manage consolidation"}
              </p>
            </div>
            <div className="flex space-x-3">
              {hasSelection && (
                <button
                  onClick={openQuickConsolidate}
                  className="cursor-pointer px-4 py-2 flex items-center space-x-2 rounded-lg border bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100"
                >
                  <Zap size={16} />
                  <span>Quick Consolidate</span>
                </button>
              )}
              <button
                onClick={openConsolidate}
                className="cursor-pointer px-4 py-2 flex items-center space-x-2 rounded-lg border bg-green-50 text-green-700 border-green-200 hover:bg-green-100"
              >
                <MoveHorizontal size={16} />
                <span>Consolidate</span>
              </button>
              <button
                className="px-4 py-2 flex items-center space-x-2 rounded-lg border bg-red-400 text-white cursor-pointer"
                onClick={openDeconsolidate}
              >
                <Share2 size={16} />
                <span>Deconsolidate</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <ConsolidateInventory
        open={showConsolidateModal}
        onClose={closeConsolidate}
      />

      <DeconsolidateInventory
        open={showDeconsolidateModal}
        onCancel={closeDeconsolidate}
      />

      <QuickConsolidateModal
        open={showQuickConsolidateModal}
        onClose={() => setShowQuickConsolidateModal(false)}
        selectedPieces={selectedInventories}
        onSuccess={() => {
          setShowQuickConsolidateModal(false);
          onActionComplete?.();
        }}
      />
    </>
  );
};
